import React, { useState } from 'react'

import { string, object, bool, shape, arrayOf, number } from 'prop-types'

import { useActionsData } from 'Pods/UnifiedNavigation/Hooks/useActionsData'

import { getCartProgress } from 'Utils/getCartProgress'

import CartProgressBar from './Components/CartProgressBar/CartProgressBar'
import UserAction from './Components/UserAction/UserAction'
import UserMenu from './Components/UserMenu/UserMenu'

let CartWidget
let MessagesWidget
let NotificationsWidget

function UserActions({ actionsLinks, actionsConfig, locale }) {
  const { isCurrentUser, newRoyaltyUi, showCartAds, subscriptionTopbar } = actionsConfig

  const [isCartWidgetLoaded, setIsCartWidgetLoaded] = useState(false)
  const [isMessagesWidgetLoaded, setIsMessagesWidgetLoaded] = useState(false)
  const [isNotificationsWidgetLoaded, setIsNotificationsWidgetLoaded] = useState(false)

  const {
    cart: fetchedCart,
    messages,
    notifications,
    fetchNextCartDataPage,
    boostCredits,
    userModelsPublished,
  } = useActionsData(isCurrentUser)

  function handleWidgetLoad(widgetId, callback) {
    if (widgetId === 'cart-action' && !CartWidget && !isCartWidgetLoaded) {
      import('Pods/LazyLoaded/CartWidget/CartWidget')
        .then((module) => {
          CartWidget = module.default
          setIsCartWidgetLoaded(true)
          callback?.()
        })
        .catch((error) => {
          console.error('Failed to load CartWidget:', error)
        })
    }

    if (widgetId === 'inbox-action' && !MessagesWidget && !isMessagesWidgetLoaded) {
      import('Pods/LazyLoaded/MessagesWidget/MessagesWidget')
        .then((module) => {
          MessagesWidget = module.default
          setIsMessagesWidgetLoaded(true)
          callback?.()
        })
        .catch((error) => {
          console.error('Failed to load MessagesWidget:', error)
        })
    }

    if (widgetId === 'notifications-action' && !NotificationsWidget && !isNotificationsWidgetLoaded) {
      import('Pods/LazyLoaded/NotificationsWidget/NotificationsWidget')
        .then((module) => {
          NotificationsWidget = module.default
          setIsNotificationsWidgetLoaded(true)
          callback?.()
        })
        .catch((error) => {
          console.error('Failed to load NotificationsWidget:', error)
        })
    }
  }

  const countsMap = {
    'cart-action': fetchedCart.meta.size,
    'boost-credits-action': boostCredits,
    'inbox-action': messages.totalUnreadMessages,
    'notifications-action': notifications.feedSize,
  }

  function prepareTrackViewItemList(attributes) {
    let trackableAttributes = {}
    const searchQuery = attributes.searchQuery || ''

    trackableAttributes = {
      item_id: String(attributes.id),
      item_name: attributes.title,
      affiliation: attributes.affiliation || '',
      currency: 'USD',
      index: attributes.index,
      item_brand: attributes.authorUsername,
      item_category: attributes.category,
      item_category2: attributes.subcategory,
      item_category3: attributes.itemCategory3,
      item_category4: attributes.itemCategory4,
      item_category5: attributes.itemCategory5,
      item_list_id: searchQuery,
      item_list_name: searchQuery,
      item_variant: attributes.productType,
      location_id: attributes.locationId && attributes.locationId.join('_'),
      price: parseFloat(attributes.price),
      quantity: 1,
    }

    trackableAttributes.discount = attributes.discount > 0 ? parseFloat(attributes.discount) : 0

    return trackableAttributes
  }

  function getUserId() {
    if (typeof window !== 'undefined') {
      // take user id from APP.config.userId - for slow application
      // or from window.pageConfig.currentUserId - for fast application
      return window.App?.config?.userId || window.pageConfig?.currentUserId
    }

    return ''
  }

  function trackCartRemoveGA4(id) {
    const items = fetchedCart.data
    const meta = fetchedCart.meta
    const currentItem = items.find((item) => Number(item.id) === id)
    const currentTrackingItem = meta.trackingCartItems.find(
      (item) => item.id === Number(currentItem.relationships.item.data.id)
    )

    if (window.dataLayer && meta) {
      const temp = prepareTrackViewItemList({
        ...currentTrackingItem,
        price: currentItem.attributes.price,
        discount: currentItem.attributes.discount,
      })

      window.dataLayer.push({
        event: 'remove_from_cart',
        user_id: getUserId(),
        ecommerce: {
          currency: 'USD',
          value: currentItem.attributes.price,
          items: [temp],
        },
        _clear: true,
      })
    }
  }

  const shouldShowCartProgress = !!fetchedCart.meta.saleThreshold
  const { meta } = fetchedCart
  const { totalPrice, saleThreshold } = meta
  const cartProgress = getCartProgress(totalPrice, saleThreshold)

  const hasCartWidgetFinishedLoading = isCartWidgetLoaded && !!CartWidget
  const hasMessagesWidgetFinishedLoading = isMessagesWidgetLoaded && !!MessagesWidget
  const hasNotificationsWidgetFinishedLoading = isNotificationsWidgetLoaded && !!NotificationsWidget

  const popoversMap = {
    'cart-action': {
      hasPopover: true,
      isLoaded: hasCartWidgetFinishedLoading,
      component: hasCartWidgetFinishedLoading ? (
        <CartWidget
          items={fetchedCart.data}
          meta={fetchedCart.meta}
          included={fetchedCart.included}
          onLoadNextPage={fetchNextCartDataPage}
          isLastPageLoaded={fetchedCart.isLastPageLoaded}
          locale={locale}
          trackCartRemoveGA4={trackCartRemoveGA4}
          shouldShowCartProgress={shouldShowCartProgress}
          showCartAds={showCartAds}
          isCurrentUser={isCurrentUser}
          onCloseCart={() => App.events.publish('top:menu:cart:close')}
        />
      ) : null,
    },
    'inbox-action': {
      hasPopover: true,
      isLoaded: hasMessagesWidgetFinishedLoading,
      component: hasMessagesWidgetFinishedLoading ? (
        <MessagesWidget
          messages={messages.data}
          included={messages.included}
          unreadCount={messages.totalUnreadMessages}
        />
      ) : null,
    },
    'notifications-action': {
      hasPopover: true,
      isLoaded: hasNotificationsWidgetFinishedLoading,
      component: hasNotificationsWidgetFinishedLoading ? (
        <NotificationsWidget notifications={notifications.data} isMaintenance={notifications.isFeedMaintenance} />
      ) : null,
    },
  }

  const additionalElementsMap = {
    'cart-action': <CartProgressBar shouldShowCartProgress={shouldShowCartProgress} cartProgress={cartProgress} />,
  }

  const textLabels = {
    'boost-credits-action': 'Boost Sales',
    'get-subscription-action': 'Get Subscription',
  }

  const resolvedActionsLinks = subscriptionTopbar
    ? actionsLinks.map((link) => (
      link.id === 'boost-credits-action'
        ? { id: 'get-subscription-action', href: '/subscription', icon: 'discount', isForUser: false }
        : link
    ))
    : actionsLinks

  return (
    <div className="wayfinder__actions fast-options-dropdowns">
      <ul className="wayfinder__actions-list">
        {resolvedActionsLinks
          .filter(({ id }) => {
            if (id === 'boost-credits-action' && userModelsPublished === 0) {
              return false
            }
            return true
          })
          .map(({ id, ...rest }) => (
            <UserAction
              bottomElement={additionalElementsMap[id] || null}
              isHidden={id === 'credits-action' && !fetchedCart.meta.displayLoyaltyWidget}
              key={id}
              id={id}
              isCurrentUser={isCurrentUser}
              notificationsCount={countsMap[id]}
              popoverComponent={popoversMap[id]?.component}
              isPopoverLoaded={popoversMap[id]?.isLoaded}
              hasPopover={popoversMap[id]?.hasPopover}
              text={textLabels[id]}
              onWidgetLoad={handleWidgetLoad}
              {...rest}
            />
          ))}
      </ul>

      <UserMenu isCurrentUser={isCurrentUser} locale={locale} newRoyaltyUi={newRoyaltyUi} />
    </div>
  )
}

UserActions.propTypes = {
  actionsLinks: arrayOf(
    shape({
      id: string,
      href: string,
      icon: string,
      data: shape({
        trackText: string,
        trackLabel: string,
        avoidNavigation: bool,
      }),
    })
  ).isRequired,
  actionsConfig: shape({
    cart: object,
    inboxSize: number,
    isCurrentUser: bool,
    newRoyaltyUi: bool,
    showCartAds: bool,
    subscriptionTopbar: bool,
  }).isRequired,
  locale: string.isRequired,
}

export default UserActions
